import { FC, useState } from 'react'

import {
  GeneratedSite, SitePlan, SitePriceVariant, formatEuros,
} from '@api/generated-site'
import { useSiteOffers, useSubscribeToSite } from '@hooks/queries/generated-site'
import Radio from '../Radio/Radio'
import SiteCountdown from './SiteCountdown'

type Props = {
  site: GeneratedSite
  // grille de l'A/B test de prix affectée au site par le serveur (site.priceVariant)
  priceVariant: SitePriceVariant
  onClose: () => void
  onSubscribed: () => void
}

// essai gratuit : le badge du plan mensuel l'annonce (« 7 jours offerts »), les badges des plans
// annuels restent ; trialDays vaut 0 pour une réactivation (site déjà activé une fois)
const badgeFor = (plan: SitePlan, trialDays: number) => (trialDays > 0 ? `${trialDays} jours offerts` : plan.badge)

const planSubline = (plan: SitePlan, trialDays: number) => {
  if (trialDays > 0) return `0 € aujourd’hui, puis ${formatEuros(plan.amountHt)} HT/mois · sans engagement`
  return plan.months > 1 ? `soit ${formatEuros(plan.monthlyEquivalentHt)} HT/mois` : 'sans engagement'
}

const formatDate = (date: Date) => date.toLocaleDateString('fr-FR')
const trialEndDate = (trialDays: number) => formatDate(
  new Date(Date.now() + trialDays * 24 * 3600 * 1000),
)

type PlanCardProps = { plan: SitePlan, trialDays: number, selected: boolean, onSelect: () => void }

const PlanCard: FC<PlanCardProps> = ({
  plan, trialDays, selected, onSelect,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={`relative w-full text-left rounded-2xl border-2 px-4 py-4 transition-colors ${selected ? 'border-black bg-black text-white' : 'border-black/10 bg-white hover:border-black/40'}`}
  >
    {badgeFor(plan, trialDays) && (
      <span className={`absolute -top-3 right-4 rounded-full px-3 py-1 text-[11px] font-semibold ${selected ? 'bg-white text-black' : 'bg-black text-white'}`}>
        {badgeFor(plan, trialDays)}
      </span>
    )}
    <div className="flex items-center gap-3">
      <span className={selected ? 'invert' : ''}><Radio selected={selected} /></span>
      <div className="grow">
        <div className="flex items-baseline justify-between gap-3">
          <span className="font-aeonik text-lg font-medium">{plan.label}</span>
          <span className="font-aeonik text-lg font-medium tabular-nums">
            {formatEuros(plan.amountHt)}
            <span className={`text-xs font-normal ml-1 ${selected ? 'text-white/60' : 'text-black/50'}`}>HT</span>
          </span>
        </div>
        <div className={`mt-0.5 flex items-center justify-between text-xs ${selected ? 'text-white/70' : 'text-black/60'}`}>
          <span>
            {planSubline(plan, trialDays)}
            {plan.discountPercent > 0 && (
              <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${selected ? 'bg-white/15' : 'bg-black/5'}`}>
                −{plan.discountPercent}%
              </span>
            )}
          </span>
          <span>{plan.credits} crédits inclus</span>
        </div>
      </div>
    </div>
  </button>
)

const errorMessages: Record<string, string> = {
  card_declined: 'Votre banque a refusé le paiement. Changez de carte dans votre espace client puis réessayez.',
  no_payment_method: 'Aucune carte enregistrée sur votre compte : ajoutez-en une dans « Moyen de paiement ».',
  coworking_unavailable: 'Cette offre n’est pas encore disponible pour votre adresse : écrivez-nous depuis la rubrique Aide.',
  already_purchased: 'Votre site est déjà activé.',
}

// panneau d'activation du site : 4 durées, paiement en un clic sur la carte enregistrée
const OfferPanel: FC<Props> = ({
  site, priceVariant, onClose, onSubscribed,
}) => {
  const offers = useSiteOffers(priceVariant)
  const subscribe = useSubscribeToSite()
  // plan mensuel présélectionné (décision du 31/10/2026) : le client voit d'abord le prix
  // sans engagement
  const [planKey, setPlanKey] = useState<SitePlan['key']>('month')

  const plans = offers.data?.plans || []
  const selected = plans.find((plan) => plan.key === planKey) || plans[0]
  // réactivation d'un site déjà activé une fois (essai arrêté, abonnement fini) : pas de second
  // essai, comme côté serveur
  const reactivation = !!site.purchasedAt
  const trialDaysOf = (plan: SitePlan) => (reactivation ? 0 : plan.trialDays)
  const trial = !!selected && trialDaysOf(selected) > 0
  const eyebrow = reactivation ? 'Remettre mon site en ligne' : 'Mettre mon site en ligne'
  const errorCode = (subscribe.error as { response?: { data?: { code?: string } } })?.response?.data?.code
  const errorMessage = subscribe.isError ? (errorMessages[errorCode || ''] || 'Une erreur est survenue, réessayez dans un instant.') : null

  const onConfirm = async () => {
    if (!selected || !offers.data) return
    try {
      // la grille facturée est celle enregistrée sur le site côté serveur, la même que celle affichée
      await subscribe.mutateAsync({ plan: selected.key })
      onSubscribed()
    } catch (e) {
      // message affiché via errorMessage
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-0 md:p-6">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full md:max-w-[560px] max-h-[94vh] overflow-y-auto rounded-t-3xl md:rounded-3xl bg-white p-6 md:p-8 shadow-2xl animate-fade-up">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 h-9 w-9 rounded-full bg-black/5 text-lg leading-none hover:bg-black/10">×</button>

        <div className="text-[11px] uppercase tracking-[0.2em] text-black/50 mb-2">{eyebrow}</div>
        <h2 className="font-aeonik text-2xl md:text-[28px] font-medium leading-tight mb-2">
          {site.commercialBrand || 'Votre site'} en ligne dans une minute.
        </h2>
        <p className="text-sm text-black/60 mb-5">
          Hébergement, nom de domaine, mises à jour par IA et formulaire de contact inclus. Réglé avec votre carte
          {site.paymentLast4 ? <> enregistrée •••• {site.paymentLast4}</> : ' enregistrée'}, sans ressaisie.
          {!reactivation && (offers.data?.trialDays || 0) > 0 && ' Le mensuel commence par un essai gratuit : rien n’est prélevé aujourd’hui.'}
        </p>

        {site.expiresAt && !site.purchasedAt && (
          <div className="mb-5"><SiteCountdown expiresAt={site.expiresAt} variant="light" /></div>
        )}

        <div className="flex flex-col gap-3 mt-2" role="radiogroup">
          {offers.isPending && <div className="h-24 rounded-2xl bg-black/5 animate-pulse" />}
          {plans.map((plan) => (
            <PlanCard key={plan.key} plan={plan} trialDays={trialDaysOf(plan)} selected={selected?.key === plan.key} onSelect={() => setPlanKey(plan.key)} />
          ))}
        </div>

        {selected && (
          <div className="mt-5 rounded-2xl bg-black/[0.04] px-4 py-3 text-xs text-black/70 flex justify-between gap-3">
            {trial ? (
              <span>
                Aujourd’hui :
                {' '}
                <strong className="text-black">0 €</strong>
                {` · puis ${formatEuros(selected.amountHt)} HT (${formatEuros(selected.amountTtc)} TTC) par mois à partir du ${trialEndDate(trialDaysOf(selected))}`}
              </span>
            ) : (
              <span>Total {selected.label} : <strong className="text-black">{formatEuros(selected.amountHt)} HT</strong> · {formatEuros(selected.amountTtc)} TTC</span>
            )}
            <span className="shrink-0">{selected.credits} crédits de modification</span>
          </div>
        )}

        {errorMessage && <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{errorMessage}</div>}

        <button
          type="button"
          onClick={onConfirm}
          disabled={!selected || subscribe.isPending || !site.offerAvailable}
          className="mt-5 w-full rounded-2xl bg-black py-4 font-aeonik text-base font-medium text-white hover:bg-black/90 disabled:opacity-50"
        >
          {subscribe.isPending && 'Activation en cours…'}
          {!subscribe.isPending && (trial ? 'Commencer mon essai gratuit' : `Activer mon site · ${selected ? formatEuros(selected.amountHt) : ''} HT`)}
        </button>
        {!site.offerAvailable && (
          <div className="mt-3 text-xs text-black/60">{errorMessages.coworking_unavailable}</div>
        )}
        <div className="mt-3 text-center text-[11px] text-black/50">
          {trial
            ? 'Paiement sécurisé par Stripe. Arrêtez l’essai en un clic depuis votre espace client avant la fin : rien ne vous sera facturé.'
            : 'Paiement sécurisé par Stripe. Résiliable depuis votre espace client, les crédits inclus restent acquis pour la durée choisie.'}
        </div>
      </div>
    </div>
  )
}

export default OfferPanel
